import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarCheckIcon, XIcon } from 'lucide-react';
interface BookingNotification {
  id: number;
  name: string;
  city: string;
  service: string;
  price: string;
}
const NAMES = ['Carlos M.', 'Miguel R.', 'José L.', 'David P.', 'Andrés G.', 'Luis F.', 'Roberto S.', 'Juan C.', 'Diego V.', 'Fernando T.', 'Ricardo A.', 'Alejandro H.', 'Daniel M.', 'Sebastián R.', 'Gabriel N.', 'Héctor P.', 'Óscar D.', 'Raúl E.', 'Marcos J.', 'Iván Q.', 'Eduardo B.', 'Javier K.', 'Emilio W.', 'Santiago Z.', 'Tomás O.'];
const CITIES = ['Lehigh Acres', 'Fort Myers', 'Cape Coral', 'Naples', 'Bonita Springs', 'Estero', 'Immokalee', 'Golden Gate', 'San Carlos Park', 'North Fort Myers', 'Alva', 'Buckingham', 'Gateway', 'Iona', 'Whiskey Creek'];
const SERVICES = [{
  name: 'Corte Clásico + Fade',
  price: '$25'
}, {
  name: 'Corte + Diseño de Barba',
  price: '$35'
}, {
  name: 'Fade Perfecto',
  price: '$30'
}, {
  name: 'Trenzas + Fade',
  price: '$45'
}, {
  name: 'Afeitado Clásico con Navaja',
  price: '$20'
}, {
  name: 'Corte + Color',
  price: '$50'
}, {
  name: 'Diseño Custom',
  price: '$40'
}, {
  name: 'Corte de Niño',
  price: '$18'
}];
const GALLERY_IMAGES = ['https://cdn.magicpatterns.com/uploads/nuHrrv392TZ4RFJQkdY9ho/b01.png', 'https://cdn.magicpatterns.com/uploads/5p86RdE7tDRXJ6RAod5mn8/b02.png', 'https://cdn.magicpatterns.com/uploads/cjxvyjEMerZbJsXMcDwSTb/b03.png', 'https://cdn.magicpatterns.com/uploads/rwzrLvNYjsnqcEWJp5D5e6/b04.png', 'https://cdn.magicpatterns.com/uploads/3iPxYiDhmXBaBcPVaS6EaS/b05.png', 'https://cdn.magicpatterns.com/uploads/a4kQ5i7wFn3U5xJY9arD93/b06.png'];
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000; // 8-15 seconds
}
export function RecentBookingToast() {
  const [notification, setNotification] = useState<BookingNotification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const recentNames = useRef<string[]>([]);
  const idCounter = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const pickName = useCallback((): string => {
    const available = NAMES.filter(n => !recentNames.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = getRandomItem(pool);
    recentNames.current.push(name);
    if (recentNames.current.length > 10) {
      recentNames.current.shift();
    }
    return name;
  }, []);
  const createNotification = useCallback((): BookingNotification => {
    const name = pickName();
    const city = getRandomItem(CITIES);
    const service = getRandomItem(SERVICES);
    idCounter.current += 1;
    return {
      id: idCounter.current,
      name,
      city,
      service: service.name,
      price: service.price
    };
  }, [pickName]);
  const showNotification = useCallback(() => {
    setDismissed(false);
    setNotification(createNotification());
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  }, [createNotification]);
  const scheduleNext = useCallback(() => {
    timerRef.current = setTimeout(() => {
      showNotification();
      scheduleNext();
    }, getRandomInterval());
  }, [showNotification]);
  useEffect(() => {
    // First notification after 3 seconds
    const initialTimer = setTimeout(() => {
      showNotification();
      scheduleNext();
    }, 3000);
    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNotification, scheduleNext]);
  const handleDismiss = () => {
    setDismissed(true);
    setNotification(null);
  };
  const thumbnail = getRandomItem(GALLERY_IMAGES);
  const motionProps = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.15
    }
  } : {
    initial: {
      opacity: 0,
      y: 60,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.95
    },
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] pointer-events-none w-full max-w-sm px-4" data-id="element-250">
      <AnimatePresence mode="wait" data-id="element-251">
        {notification && !dismissed && <motion.div key={notification.id} {...motionProps} className="pointer-events-auto bg-black/95 backdrop-blur-md border border-delux-gold/30 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.5),0_0_15px_rgba(200,164,90,0.15)] overflow-hidden" data-id="element-252">
            <div className="flex items-center gap-3 p-3" data-id="element-253">
              {/* Thumbnail */}
              <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-delux-gold/20" data-id="element-254">
                <img src={thumbnail} alt="Corte reciente" className="w-full h-full object-cover object-top" data-id="element-255" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-256">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-257">
                  <CalendarCheckIcon className="w-3.5 h-3.5 text-delux-gold flex-shrink-0" data-id="element-258" />
                  <span className="font-body text-[11px] text-delux-gold/70 uppercase tracking-wider" data-id="element-259">
                    Nueva cita agendada
                  </span>
                </div>
                <p className="font-heading text-sm text-delux-white leading-snug truncate" data-id="element-260">
                  <span className="font-bold" data-id="element-261">{notification.name}</span>
                  <span className="text-delux-body" data-id="element-262"> en </span>
                  <span data-id="element-263">{notification.city}</span>
                </p>
                <p className="font-body text-xs text-delux-body/80 truncate mt-0.5" data-id="element-264">
                  {notification.service} —{' '}
                  <span className="text-delux-gold font-medium" data-id="element-265">
                    {notification.price}
                  </span>
                </p>
              </div>

              {/* Dismiss */}
              <button onClick={handleDismiss} className="flex-shrink-0 text-delux-body/40 hover:text-delux-gold transition-colors p-1" aria-label="Cerrar notificación" data-id="element-266">
                <XIcon className="w-4 h-4" data-id="element-267" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-0.5 bg-delux-gold/10" data-id="element-268">
              <motion.div className="h-full bg-delux-gold/50" initial={{
            width: '100%'
          }} animate={{
            width: '0%'
          }} transition={{
            duration: 4,
            ease: 'linear'
          }} data-id="element-269" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}